import { memo } from 'react';

import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Text from '@ui/text/text/text';
import { ICONS } from '@ui/icons/types';
import RegistrationLimitToggle from './registration-limit-toggle';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import SettingsToggle from '../../../../controls/settings-toggle';
import { Registration } from '@/types/registration.types';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import IconWithBackdrop from '@ui/icons/icon-with-backdrop';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';

const AdvancedRegistrationSettings = memo(() => {
  const { registrationSettings, customSsoEnabled, singleSignOnEnabled, registrationOn } =
    useComputedRegistrationSettings();
  const isExternalRegistrationOn = !!registrationSettings?.externalRegistration?.isOn;
  const isDisabled = !!registrationSettings && !registrationOn;
  const isNativeRegistration = !customSsoEnabled && !isExternalRegistrationOn && !isDisabled;

  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);
  const dispatch = useAppDispatch();

  const toggleFeature = (value: string, on: boolean) => {
    if (!registrationSettings) return;
    const updatedSettings: Registration = {
      ...registrationSettings,
      [value as keyof Registration]: on,
    };
    dispatch(saveWorkingProjectByFields({ registration_settings: updatedSettings }));
  };

  return (
    <SlideDownCard autoGrow animateOnInitialLoad={false} open={!isDisabled && !isExternalRegistrationOn}>
      <div className="flex-column gap-8">
        <div className="flex ai-center gap-8">
          <Text tag="h5" className="H5" design="GlobalIcoText1">
            Advanced
          </Text>
          <TooltipPortal tooltipContent={<Text design="GlobalIcoText1">Advanced Registration Settings</Text>}>
            <IconWithBackdrop
              name={ICONS.INFO_CIRCLE}
              size={12}
              color="var(--primitives-mode-1-gray-500)"
              backdropColor="var(--primitives-mode-1-gray-100)"
            />
          </TooltipPortal>
        </div>
        <SlideDownCard open={isNativeRegistration} autoGrow animateOnInitialLoad={false}>
          <SettingsToggle
            label="Close Registration"
            disabled={savingWorkingProject || !registrationSettings}
            value={registrationSettings?.isClosed || false}
            sKey="isClosed"
            onToggle={toggleFeature}
          />
        </SlideDownCard>
        <SettingsToggle
          label="Recaptcha"
          disabled={
            savingWorkingProject ||
            !registrationSettings ||
            (singleSignOnEnabled && !registrationSettings?.enableRecaptcha)
          }
          value={registrationSettings?.enableRecaptcha || false}
          sKey="enableRecaptcha"
          onToggle={toggleFeature}
        />
        <SlideDownCard open={isNativeRegistration} autoGrow animateOnInitialLoad={false}>
          <div className="flex-column gap-8">
            <RegistrationLimitToggle />
            <SettingsToggle
              label="Display Speakers"
              disabled={savingWorkingProject || !registrationSettings}
              value={registrationSettings?.enableSpeakers || false}
              sKey="enableSpeakers"
              onToggle={toggleFeature}
            />
            <SettingsToggle
              label="Show Overview"
              disabled={savingWorkingProject || !registrationSettings}
              value={registrationSettings?.enableOverviewContent || false}
              sKey="enableOverviewContent"
              onToggle={toggleFeature}
            />
          </div>
        </SlideDownCard>
      </div>
    </SlideDownCard>
  );
});

AdvancedRegistrationSettings.displayName = 'AdvancedRegistrationSettings';

export default AdvancedRegistrationSettings;
